import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, Tabs, Tab
} from '@mui/material';
import { useSnackbar } from 'notistack';
import cajaUsuarioService from '../../services/cajaUsuarioService';
import GrupoMcpSearch from '../../components/GrupoMcpSearch';
import UsuarioSearch from '../../components/UsuarioSearch';

interface CajaUsuarioFormData {
  codigoCaja: number;
  usuario: string;
  nombreUsuario?: string;
  nombreCaja?: string;
}

const defaultForm: CajaUsuarioFormData = {
  codigoCaja: 0,
  usuario: ''
};

const CajaUsuarioForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { grupoMcp, usuario } = useParams();
  const [formData, setFormData] = useState<CajaUsuarioFormData>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [cajaSearchOpen, setCajaSearchOpen] = useState(false);
  const [usuarioSearchOpen, setUsuarioSearchOpen] = useState(false);

  const loadCajaUsuario = useCallback(async () => {
    if (!grupoMcp || !usuario || grupoMcp === 'nuevo' || usuario === 'nuevo') return;
    try {
      setLoading(true);
      const cajaUsuario = await cajaUsuarioService.getCajaUsuario(Number(grupoMcp), usuario);
      setFormData(cajaUsuario);
    } catch (error) {
      enqueueSnackbar('Error al cargar la asignación', { variant: 'error' });
      navigate('/sistema/caja-usuario');
    } finally {
      setLoading(false);
    }
  }, [grupoMcp, usuario, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (grupoMcp && grupoMcp !== 'nuevo' && usuario && usuario !== 'nuevo') loadCajaUsuario();
  }, [grupoMcp, usuario, loadCajaUsuario]);

  const _handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'codigoCaja') {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCajaSelect = (caja: any) => {
    setFormData(prev => ({
      ...prev,
      codigoCaja: caja.codigoCaja,
      nombreCaja: caja.nombreCaja
    }));
    setCajaSearchOpen(false);
  };

  const handleUsuarioSelect = (usuario: any) => {
    const usuarioSeleccionado = String(usuario?.usuario || usuario?.username || usuario?.id || '').trim();
    const nombreSeleccionado = String(usuario?.nombre || usuario?.name || '').trim();

    setFormData(prev => ({
      ...prev,
      usuario: usuarioSeleccionado,
      nombreUsuario: nombreSeleccionado || usuarioSeleccionado
    }));
    setUsuarioSearchOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.codigoCaja || !formData.usuario.trim()) {
        enqueueSnackbar('Complete los campos obligatorios', { variant: 'warning' });
        return;
      }

      if (grupoMcp && grupoMcp !== 'nuevo' && usuario && usuario !== 'nuevo') {
        await cajaUsuarioService.updateCajaUsuario(Number(grupoMcp), usuario, formData);
      } else {
        await cajaUsuarioService.createCajaUsuario(formData);
      }

      enqueueSnackbar('Asignación guardada exitosamente', { variant: 'success' });
      navigate('/sistema/caja-usuario');
    } catch (error: any) {
      console.error('Error al guardar asignación:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar la asignación', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {grupoMcp && grupoMcp !== 'nuevo' && usuario && usuario !== 'nuevo' ? 'Editar Asignación' : 'Nueva Asignación'}
          </Typography>

          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
            <Tab label="General" />
            <Tab label="Configuración" />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {currentTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1}>
                    <TextField 
                      label="Caja" 
                      value={formData.nombreCaja || formData.codigoCaja || ''} 
                      fullWidth 
                      required
                      disabled
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button 
                      variant="outlined" 
                      onClick={() => setCajaSearchOpen(true)}
                      disabled={!!(grupoMcp && grupoMcp !== 'nuevo' && usuario && usuario !== 'nuevo')}
                      sx={{ minWidth: 120 }}
                    >
                      Buscar
                    </Button>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1}>
                    <TextField 
                      label="Usuario" 
                      value={formData.usuario || ''} 
                      fullWidth 
                      required
                      disabled
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button 
                      variant="outlined" 
                      onClick={() => setUsuarioSearchOpen(true)}
                      disabled={!!(grupoMcp && grupoMcp !== 'nuevo' && usuario && usuario !== 'nuevo')}
                      sx={{ minWidth: 120 }}
                    >
                      Buscar
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            )}

            {currentTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Configuración adicional de la asignación
                  </Typography>
                </Grid>
              </Grid>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/sistema/caja-usuario')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <GrupoMcpSearch
        open={cajaSearchOpen}
        onClose={() => setCajaSearchOpen(false)}
        onSelect={handleCajaSelect}
      />

      <UsuarioSearch
        open={usuarioSearchOpen}
        onClose={() => setUsuarioSearchOpen(false)}
        onSelect={handleUsuarioSelect}
      />
    </Box>
  );
};

export default CajaUsuarioForm;
